import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import MainLayout from '../../components/Layout/MainLayout';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { api } from '../../services/api';
import { Add, Edit, Delete, DragIndicator } from '@mui/icons-material';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const FunnelsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const FunnelCard = styled(Card)`
  padding: ${props => getTheme(props.theme?.isDark).spacing.md};
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 15px rgba(25, 184, 136, 0.2);
    `}
  }
`;

const FunnelIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary}20;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.color || getTheme(props.theme?.isDark).colors.primary};
  flex-shrink: 0;
`;

const FunnelInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FunnelName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const FunnelDetails = styled.div`
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
  flex-wrap: wrap;
`;

const FunnelActions = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const ColorPreview = styled.div`
  width: 24px;
  height: 24px;
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.sm};
  background-color: ${props => props.color};
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const CustomerFunnelsList = () => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const navigate = useNavigate();
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFunnels();
  }, []);

  const fetchFunnels = async () => {
    try {
      setLoading(true);
      const response = await api.getCustomerFunnels({ limit: 100 });
      setFunnels(response.results || []);
    } catch (error) {
      console.error('Error fetching funnels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this funnel?')) {
      try {
        await api.deleteCustomerFunnel(id);
        fetchFunnels();
      } catch (error) {
        console.error('Error deleting funnel:', error);
        alert('Failed to delete funnel');
      }
    }
  };

  const getIconComponent = (iconName) => {
    const IconComponent = require('@mui/icons-material')[iconName] || require('@mui/icons-material').People;
    return IconComponent;
  };

  const getConditionsSummary = (conditions) => {
    const parts = [];
    if (conditions.status && conditions.status.length > 0) {
      parts.push(`Status: ${conditions.status.join(', ')}`);
    }
    if (conditions.hasActiveSubscription !== null) {
      parts.push(`Active Subscription: ${conditions.hasActiveSubscription ? 'Yes' : 'No'}`);
    }
    if (conditions.hasAnySubscription !== null) {
      parts.push(`Any Subscription: ${conditions.hasAnySubscription ? 'Yes' : 'No'}`);
    }
    if (conditions.lastLoginDays !== null) {
      parts.push(`Last Login: ${conditions.lastLoginDays} days`);
    }
    return parts.length > 0 ? parts.join(' • ') : 'No conditions';
  };

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>Customer Funnels</Title>
          <Button onClick={() => navigate('/admin/customerFunnels/new')} theme={theme}>
            <Add /> Create New
          </Button>
        </PageHeader>

        {loading ? (
          <LoadingContainer theme={theme}>Loading funnels...</LoadingContainer>
        ) : funnels.length === 0 ? (
          <EmptyState theme={theme}>
            <p>No funnels found</p>
            <Button onClick={() => navigate('/admin/customerFunnels/new')} theme={theme} style={{ marginTop: '16px' }}>
              <Add /> Create First Funnel
            </Button>
          </EmptyState>
        ) : (
          <FunnelsList theme={theme}>
            {funnels.map((funnel) => {
              const IconComponent = getIconComponent(funnel.icon);
              // Safely extract ID - handle both _id and id, ensure it's a string
              let funnelId;
              if (funnel._id) {
                funnelId = typeof funnel._id === 'string' ? funnel._id : funnel._id.toString();
              } else if (funnel.id) {
                funnelId = typeof funnel.id === 'string' ? funnel.id : (funnel.id.toString ? funnel.id.toString() : String(funnel.id));
              } else {
                console.error('Funnel missing ID:', funnel);
                return null;
              }
              
              return (
                <FunnelCard
                  key={funnelId}
                  theme={theme}
                  onClick={() => navigate(`/admin/customerFunnels/${funnelId}`)}
                >
                  <DragIndicator style={{ color: getTheme(isDark).colors.textSecondary, cursor: 'grab' }} />
                  <FunnelIcon theme={theme} color={funnel.color}>
                    <IconComponent />
                  </FunnelIcon>
                  <FunnelInfo>
                    <FunnelName theme={theme}>{funnel.name}</FunnelName>
                    <FunnelDetails theme={theme}>
                      <span>Order: {funnel.order}</span>
                      {funnel.group && <span>Group: {funnel.group}</span>}
                      <span>Active: {funnel.isActive ? 'Yes' : 'No'}</span>
                      <span>{getConditionsSummary(funnel.conditions)}</span>
                    </FunnelDetails>
                  </FunnelInfo>
                  <ColorPreview theme={theme} color={funnel.color} />
                  <FunnelActions>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/admin/customerFunnels/${funnelId}`);
                      }}
                      theme={theme}
                      variant="outline"
                    >
                      <Edit />
                    </Button>
                    <Button
                      onClick={(e) => handleDelete(funnelId, e)}
                      theme={theme}
                      variant="danger"
                    >
                      <Delete />
                    </Button>
                  </FunnelActions>
                </FunnelCard>
              );
            })}
          </FunnelsList>
        )}
      </PageContainer>
    </MainLayout>
  );
};

export default CustomerFunnelsList;

